import { motion } from "framer-motion";
import {
  Truck, Users, Package, ShoppingBag, Brain, FileText,
  Activity, Workflow, Shield, TrendingUp, Sparkles,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

const ECOSYSTEM = [
  { icon: Truck, label: "Exporters", desc: "Increase throughput. Improve cash flow. Scale faster.", color: "text-purple-400" },
  { icon: Users, label: "Importers", desc: "Reduce receivables. Improve liquidity. Buy with confidence.", color: "text-blue-400" },
  { icon: Package, label: "Distributors", desc: "Optimize inventory. Reliable supply. Stronger margins.", color: "text-cyan-400" },
  { icon: ShoppingBag, label: "Retail Buyers", desc: "Better availability. Predictable supply. Growth enablement.", color: "text-pink-400" },
  { icon: Brain, label: "Commercial Intelligence", desc: "AI scoring. Risk insights. Better decisions.", color: "text-[#22C55E]" },
  { icon: FileText, label: "Documents", desc: "Digitized. Verified. Always accessible.", color: "text-amber-400" },
  { icon: Activity, label: "Shipment Milestones", desc: "Real-time tracking. Milestone validation. Complete visibility.", color: "text-orange-400" },
  { icon: Workflow, label: "Settlement Workflows", desc: "Faster collections. Automated workflows. Lower friction.", color: "text-rose-400" },
  { icon: Shield, label: "Trust & Compliance", desc: "Verified partners. Secure data. Stronger confidence.", color: "text-emerald-400" },
  { icon: TrendingUp, label: "Investors", desc: "Access scalable flows. Recurring revenues. High quality assets.", color: "text-[#22C55E]" },
];

const DIFFERENTIATORS = [
  { title: "End-to-End Ecosystem", desc: "From order to settlement and beyond." },
  { title: "AI-Powered Intelligence", desc: "Commercial intelligence at the core." },
  { title: "Real-Time Visibility", desc: "Live tracking and milestone-based control." },
  { title: "Trust & Compliance", desc: "Verified partners. Secure and compliant by design." },
  { title: "Scalable Infrastructure", desc: "Built to scale across categories, corridors and continents." },
];

export function WhatIsDeltaGreenX() {
  return (
    <section id="what-is" className="relative py-24 md:py-32 overflow-hidden" data-testid="what-is-section">
      <div className="absolute inset-0 [background-image:radial-gradient(circle_at_50%_30%,rgba(34,197,94,0.08),transparent_60%)]" />
      <div className="relative max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="flex items-center gap-2 mb-4">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          <span className="text-[#22C55E] text-[10px] font-semibold tracking-[0.3em] uppercase">What is Delta GreenX</span>
        </motion.div>
        <motion.h2 {...fade(0.05)} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white max-w-4xl">
          The intelligent operating <br className="hidden md:block" />
          <span className="text-[#22C55E]">infrastructure</span> for global food trade.
        </motion.h2>
        <motion.p {...fade(0.1)} className="mt-6 text-[#C5D5CC] text-base lg:text-lg max-w-3xl leading-relaxed">
          Delta GreenX connects the entire food trade ecosystem into one intelligent operating infrastructure.
          We unify people, processes, documents, data and capital flows — using AI, automation and real-time visibility
          to drive trust, efficiency and faster settlements.
        </motion.p>

        {/* Ecosystem grid */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ecosystem-grid">
          {ECOSYSTEM.map((e, i) => (
            <motion.div
              key={e.label}
              {...fade(0.15 + i * 0.03)}
              className="border border-[#1A2E22] bg-[#09120D] p-5 hover:border-[#22C55E]/30 transition-colors"
            >
              <e.icon className={`w-5 h-5 ${e.color} mb-3`} />
              <p className="text-white font-['Outfit'] font-semibold text-sm mb-1">{e.label}</p>
              <p className="text-[#A1B2A8] text-xs leading-snug">{e.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* The line */}
        <motion.div {...fade(0.45)} className="mt-14 border-y border-[#22C55E]/30 py-8 text-center bg-[#22C55E]/[0.03]">
          <p className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white tracking-tight">
            We are <span className="text-[#A1B2A8]/50 line-through">not a marketplace</span>.{" "}
            We are <span className="text-[#A1B2A8]/50 line-through">not a lender</span>.
          </p>
          <p className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#22C55E] tracking-tight mt-2">
            We are the infrastructure trade flows through.
          </p>
        </motion.div>

        {/* Differentiators */}
        <div className="mt-14">
          <motion.p {...fade(0)} className="text-[#A1B2A8] text-[10px] font-semibold uppercase tracking-[0.3em] mb-5 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#22C55E]" /> What makes Delta GreenX different
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {DIFFERENTIATORS.map((d, i) => (
              <motion.div key={d.title} {...fade(0.05 + i * 0.05)}
                className="border border-[#1A2E22] bg-[#09120D] p-5 hover:border-[#22C55E]/40 transition-colors">
                <p className="text-[#22C55E] font-['Outfit'] font-bold text-sm mb-1">{d.title}</p>
                <p className="text-[#A1B2A8] text-xs leading-snug">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
